import { getDeals } from "api/requests/getDeals";
import { getPromotions } from "api/requests/getPromotions";
import { getDealsData, getPromotionsData } from "redux/selectors/deals";
import { NextPageContext } from "next";
import { Deal } from "custom-types/Deals";
import { MappedPromotion } from "custom-types/Promotions";
import { Coordinates } from "custom-types/Coordinates";

export type DealsState = {
  deals: Deal[];
  dealCategories: string[];
  promotions: MappedPromotion[];
  moreDealsAvailable: boolean;
  morePromosAvailable: boolean;
};

export const initialState: DealsState = {
  deals: [],
  promotions: [],
  dealCategories: [],
  moreDealsAvailable: true,
  morePromosAvailable: true,
};

export const DEALS_LOADED = "DEALS_LOADED";
export const APPEND_DEALS = "APPEND_DEALS";
export const PROMOTIONS_LOADED = "PROMOTIONS_LOADED";
export const APPEND_PROMOTIONS = "APPEND_PROMOTIONS";

export type DealsLoadedAction = {
  type: typeof DEALS_LOADED;
  deals: Deal[];
  dealCategories: string[];
  take: number;
};

export type AppendDealsAction = {
  type: typeof APPEND_DEALS;
  newDeals: Deal[];
  take: number;
};

export type PromotionsLoadedAction = {
  type: typeof PROMOTIONS_LOADED;
  promotions: MappedPromotion[];
};

export type AppendPromotionsAction = {
  type: typeof APPEND_PROMOTIONS;
  newPromotions: MappedPromotion[];
  take: number;
};

export type DealsActions =
  | DealsLoadedAction
  | AppendDealsAction
  | PromotionsLoadedAction
  | AppendPromotionsAction;

export const getDealsState = async (
  ctx: NextPageContext,
  coordinates: Coordinates,
  countryCode: string
) => {
  const { req } = ctx;

  const { splitTreatments }: any = req || {};
  const randomize =
    splitTreatments?.globalSearch_dealsSort_frontend?.treatment === "on";

  const { dispatch, getState } = ctx.store;
  const currentState = getState();
  const currentDeals = getDealsData(currentState);

  if (!currentDeals.length) {
    const { deals, dealCategories } = await getDeals(
      coordinates,
      countryCode,
      0,
      60,
      !!randomize
    );

    dispatch<DealsLoadedAction>({
      type: DEALS_LOADED,
      deals,
      dealCategories,
      take: 60,
    });
  }
};

export const getPromotionsState = async (
  ctx: NextPageContext,
  coordinates: Coordinates,
  countryCode: string
) => {
  const { dispatch, getState } = ctx.store;
  const currentState = getState();
  const currentPromotions = getPromotionsData(currentState);

  if (!currentPromotions.length) {
    const { promotions } = await getPromotions(coordinates, countryCode);

    dispatch<PromotionsLoadedAction>({
      type: PROMOTIONS_LOADED,
      promotions,
    });
  }
};

export const dealsReducer = (
  state = initialState,
  action: DealsActions
): DealsState => {
  switch (action.type) {
    case DEALS_LOADED:
      return {
        ...state,
        deals: action.deals,
        dealCategories: action.dealCategories,
        moreDealsAvailable: (action.deals || []).length >= action.take,
      };
    case APPEND_DEALS:
      return {
        ...state,
        deals: [...state.deals, ...action.newDeals],
        moreDealsAvailable: (action.newDeals || []).length >= action.take,
      };
    case PROMOTIONS_LOADED:
      return {
        ...state,
        promotions: action.promotions,
      };
    case APPEND_PROMOTIONS:
      return {
        ...state,
        promotions: [...state.promotions, ...action.newPromotions],
        morePromosAvailable: (action.newPromotions || []).length >= action.take,
      };
    default:
      return state;
  }
};

export default dealsReducer;
